// src/components/auth/AuthDebug.js
import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, Alert, Divider, Chip } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const AuthDebug = () => {
    const [localStorageData, setLocalStorageData] = useState({});
    const [decodedToken, setDecodedToken] = useState(null);
    const { user, loading, refreshAuth } = useAuth();

    // Cargar datos cuando el componente se monta
    useEffect(() => {
        loadData();
    }, []);

    // Función para cargar datos del localStorage y decodificar token
    const loadData = () => {
        try {
            // Obtener datos del localStorage
            const token = localStorage.getItem('token');
            const userData = localStorage.getItem('user');
            const clientToken = localStorage.getItem('clientToken');
            const clientData = localStorage.getItem('clientData');

            setLocalStorageData({
                token: token ? token.substring(0, 15) + '...' : null,
                userData: userData ? JSON.parse(userData) : null,
                clientToken: clientToken ? clientToken.substring(0, 15) + '...' : null,
                clientData: clientData ? JSON.parse(clientData) : null,
            });

            // Decodificar el token JWT (solo para diagnóstico)
            if (token) {
                try {
                    const payloadBase64 = token.split('.')[1];
                    const payload = JSON.parse(atob(payloadBase64));
                    setDecodedToken(payload);
                } catch (e) {
                    console.error('Error decodificando token:', e);
                    setDecodedToken({ error: 'Error al decodificar token' });
                }
            }
        } catch (error) {
            console.error('Error cargando datos:', error);
        }
    };

    // Función para limpiar todos los datos de sesión
    const clearAllSessions = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('clientToken');
        localStorage.removeItem('clientData');
        loadData();
    };

    // Si el componente está cargando
    if (loading) {
        return (
            <Alert severity="info">
                Cargando estado de autenticación...
            </Alert>
        );
    }

    return (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
                Diagnóstico de Autenticación
            </Typography>

            <Alert severity={user ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {user
                    ? `Usuario autenticado: ${user.username} (${user.role})`
                    : 'No hay usuario autenticado en el contexto'
                }
            </Alert>

            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Estado de Contexto de Autenticación
                </Typography>

                {user ? (
                    <Box>
                        <Typography><strong>ID:</strong> {user.id}</Typography>
                        <Typography><strong>Usuario:</strong> {user.username}</Typography>
                        <Typography><strong>Rol:</strong> {user.role}</Typography>
                        <Typography><strong>Rol de Sistema:</strong> {user.isSystemRole ? 'Sí' : 'No'}</Typography>

                        <Typography sx={{ mt: 1 }}><strong>Permisos:</strong></Typography>
                        {Array.isArray(user.permissions) && user.permissions.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                {user.permissions.map(permission => (
                                    <Chip key={permission} label={permission} size="small" />
                                ))}
                            </Box>
                        ) : (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                No hay permisos disponibles o el formato no es válido
                            </Alert>
                        )}
                    </Box>
                ) : (
                    <Alert severity="error">Sin datos de usuario</Alert>
                )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Datos en LocalStorage
                </Typography>

                <Typography><strong>Token Admin:</strong> {localStorageData.token || 'No existe'}</Typography>
                <Typography><strong>Token Cliente:</strong> {localStorageData.clientToken || 'No existe'}</Typography>

                {localStorageData.userData && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        Datos de usuario encontrados en localStorage
                    </Alert>
                )}

                {localStorageData.clientData && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        ¡Datos de cliente encontrados! Esto podría causar conflictos.
                    </Alert>
                )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Token Decodificado (Admin)
                </Typography>

                {decodedToken ? (
                    <Box sx={{
                        maxHeight: '200px',
                        overflow: 'auto',
                        bgcolor: 'grey.100',
                        p: 2,
                        borderRadius: 1
                    }}>
                        <pre>{JSON.stringify(decodedToken, null, 2)}</pre>
                    </Box>
                ) : (
                    <Alert severity="warning">Sin token para decodificar</Alert>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button variant="contained" color="primary" onClick={refreshAuth}>
                    Refrescar Autenticación
                </Button>

                <Button variant="contained" color="secondary" onClick={loadData}>
                    Recargar Datos
                </Button>

                <Button variant="outlined" color="error" onClick={clearAllSessions}>
                    Limpiar Sesiones
                </Button>
            </Box>
        </Paper>
    );
};

export default AuthDebug;